import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Landmark, Facebook } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0073A5] border-t border-white/10 text-slate-100 text-base">
      {/* Main Footer Content */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-8">
        
        {/* Column 1: Logo */}
        <div className="flex flex-col justify-start space-y-4">
          <Link to="/" className="inline-block group">
            <img 
              src="https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/bf499a2e-a67d-4e48-b244-da86caa413fe.png" 
              alt="DAN-TEAM STAINLESS ApS" 
              className="h-[60px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </Link>
        </div>

        {/* Column 2: Kort om os */}
        <div className="space-y-4">
          <h3 className="text-white font-bold tracking-wider text-xs uppercase border-b border-white/10 pb-2">
            Kort om os
          </h3>
          <div className="text-base text-slate-200 space-y-3 font-light leading-relaxed">
            <p>
              Dan-Team Stainless drives i dag af de to ejere Søren Pedersen og Martin Vedel Bindesbøl, som begge har været i branchen for produktion og montage af rustfrit stål i fødevareindustrien gennem rigtig mange år.
            </p>
            <p>
              Martin står for montage og supervision, mens Søren er leder af værkstedet.
            </p>
          </div>
        </div>

        {/* Column 3: Kontakt os */}
        <div className="space-y-4">
          <h3 className="text-white font-bold tracking-wider text-xs uppercase border-b border-white/10 pb-2">
            Kontakt os
          </h3>
          <ul className="space-y-3 text-base text-slate-200">
            <li className="font-semibold text-white">
              DAN-TEAM STAINLESS ApS
            </li>
            <li className="font-light">
              Kurkmarkvej 6, 6093 Sjølund
            </li>
            <li>
              <a 
                href="tel:+4576991020" 
                className="hover:text-white transition-colors font-light"
              >
                (+45) 76 99 10 20
              </a>
            </li>
            <li>
              <a 
                href="mailto:info@dan-team.com" 
                className="hover:text-white transition-colors font-light"
              >
                info@dan-team.com
              </a>
            </li>
            <li className="font-light pt-1 border-t border-white/5">
              CVR: 21670405
            </li>
            <li className="pt-3 flex items-center gap-3">
              <a 
                href="https://www.facebook.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-white transition-colors flex items-center"
                title="Følg os på Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <span className="text-white/20">|</span>
              <Link 
                to="/information-in-english" 
                className="hover:opacity-80 transition-opacity flex items-center" 
                title="English"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" className="w-6 h-4 rounded-xs shadow-xs object-cover inline-block">
                  <clipPath id="s-footer">
                    <path d="M0,0 v30 h60 v-30 z"/>
                  </clipPath>
                  <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
                  <path d="M0,0 L60,30 M60,0 L0,30" stroke="#ffffff" strokeWidth="6"/>
                  <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" clipPath="url(#s-footer)"/>
                  <path d="M30,0 v30 M0,15 h60" stroke="#ffffff" strokeWidth="10"/>
                  <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
                </svg>
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 4: Åbningstider */}
        <div className="space-y-4">
          <h3 className="text-white font-bold tracking-wider text-xs uppercase border-b border-white/10 pb-2">
            Åbningstider
          </h3>
          <div className="text-base text-slate-200">
            <div className="space-y-2 font-light">
              <div>
                <span className="font-semibold text-white">Mandag – torsdag: </span>
                <span>06.30 – 16.00</span>
              </div>
              <div>
                <span className="font-semibold text-white">Fredag: </span>
                <span>Lukket</span>
              </div>
              <div>
                <span className="font-semibold text-white">Lørdag – Søndag: </span>
                <span>Lukket</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Bottom Copyright Bar */}
      <div className="bg-slate-950/40 border-t border-white/10 py-8 px-4 sm:px-6 lg:px-12 text-center md:text-right text-base text-slate-300">
        <div className="max-w-[1600px] mx-auto flex flex-col items-center md:items-end gap-4">
          {/* Inline Navigation Menu */}
          <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2 text-base font-semibold uppercase tracking-wider">
            <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            <Link to="/vaerkstedet" className="hover:text-white transition-colors">Værkstedet</Link>
            <Link to="/montageafd" className="hover:text-white transition-colors">Montageafdelingen</Link>
            <Link to="/vandskaering" className="hover:text-white transition-colors">Vandskæring</Link>
            <Link to="/om-os" className="hover:text-white transition-colors">Om os</Link>
            <Link to="/referencer" className="hover:text-white transition-colors">Referencer</Link>
            <Link to="/nyheder" className="hover:text-white transition-colors">Seneste oplæg</Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link>
          </div>

          {/* Contact Info Row */}
          <div className="flex flex-wrap justify-center md:justify-end items-center gap-x-4 gap-y-1 text-base text-slate-200/50 font-light w-full max-w-2xl md:max-w-none">
            <a href="tel:+4576991020" className="hover:text-white transition-colors font-medium">(+45) 76 99 10 20</a>
            <span className="text-white/20 hidden sm:inline">•</span>
            <span>Kurkmarkvej 6, 6093 Sjølund</span>
            <span className="text-white/20 hidden sm:inline">•</span>
            <a href="mailto:info@dan-team.com" className="hover:text-white transition-colors font-medium">info@dan-team.com</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
